import {
  GanttRowState,
  IGanttChartSerieTemporal,
} from "../../stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { resolveMissingVariableLabels } from "./missingVariableLabels";

/**
 * Escapes text before it enters a Highcharts `useHTML: true` string.
 *
 * ⚠️ This escapes ONE field today -- the variable names inside the `neverWarms`
 * label -- and NOT the event `name` beside it.
 *
 * 📖 The reason it could not be applied to the name is gone: the backend used to put
 * markup INTO the name (`value.name + "<br>" + "<strong>[No upcoming events!]<strong>"`),
 * so escaping it would have rendered those tags as literal text. The three mutation
 * sites in i4cast-workspace now leave the name alone and emit `rowState:
 * "doesNotOccur"` instead.
 *
 * ⚠️ So the injection surface on a field users type -- the event name, which reaches a
 * `useHTML: true` string -- is now CLOSEABLE and still open. Escaping it also fixes
 * names that legitimately contain `<` (homolog has `<618 mt` and
 * `Restrição para navios 340m<LOA<366`), which render broken today. Left out of this
 * change on purpose: it is a visible behaviour change on every row, not just the
 * bar-less ones, and it deserves its own PR and its own smoke.
 */
export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export type RowStatusTranslate = (
  key: string,
  options?: { defaultValue: string },
) => string;

type RowStatusItem = Pick<
  IGanttChartSerieTemporal,
  "rowState" | "missingVariables" | "error_message" | "isProcessing"
>;

/**
 * The label of every state that is just a label -- all but `ready` (draws a bar) and
 * `neverWarms` (lists variables).
 *
 * ⚠️ A `Record` over the union, not a chain of `if`s, ON PURPOSE: a state added to
 * `GanttRowState` without a label here does not compile. As a chain it compiled and
 * rendered a row with no bar and no label -- the ambiguity workspace#385 removed --
 * and the backend deploys ahead of this screen would ship exactly that.
 */
const PLAIN_ROW_STATE_LABELS: Record<
  Exclude<GanttRowState, "ready" | "neverWarms">,
  string
> = {
  // ⚠️ Distinct from `notPlanned`, and the distinction is the point:
  // here the engine DID evaluate the rule and it does not hold in the
  // window. Without this label the row would be bare -- and 📖 the
  // legacy already labels this case, so leaving it silent would lose
  // information the screen shows today.
  doesNotOccur: "oceanWeatherIntelligence.rowState.doesNotOccur",
  calculating: "oceanWeatherIntelligence.rowState.calculating",
  notPlanned: "oceanWeatherIntelligence.rowState.notPlanned",
  unavailable: "oceanWeatherIntelligence.rowState.unavailable",
  // aurora#1619: a past-date Gantt with no result for that date. Says nothing about
  // the weather (not `doesNotOccur`) and nothing will arrive (not `calculating`).
  pastUnavailable: "oceanWeatherIntelligence.rowState.pastUnavailable",
};

/**
 * The bracketed badge the Gantt's row label carries after the event name, or `""`.
 *
 * Lives outside the Highcharts formatter so the badge each state produces can be
 * tested -- inside the closure only the translations were, and a state with no branch
 * rendered a bare row with the suite green.
 *
 * workspace#385 (US2): five states, not one.
 *
 * `rowState` arrives only on engine-served area rows; the legacy path
 * keeps `isProcessing`/`error_message` untouched, so this reads as
 * before wherever the engine is not serving.
 *
 * ⚠️ `notPlanned` must NOT read as "does not occur". A row with no
 * bars and no label is the failure this story exists to remove: the
 * screen would be asserting the event never happens, when the truth
 * is that nobody has evaluated it yet.
 */
export const rowStatusText = (
  item: RowStatusItem | undefined,
  translate: RowStatusTranslate,
): string => {
  if (item?.rowState === "neverWarms") {
    // Escaped: this is the only field here that carries content from
    // the engine straight into an HTML string (`useHTML: true`). The
    // names are engine-produced today (`current_vel`, `onda`), but
    // nothing in the chain engine -> API -> here guarantees that, and
    // the guarantee is what makes it safe rather than the current data.
    // Traduzido, nao o slug cru do motor: quem le a linha le
    // "Onda - periodo de pico", nao "peak_period". O escape
    // continua por fora, sobre o rotulo ja resolvido.
    const missing = item.missingVariables?.length
      ? `: ${resolveMissingVariableLabels(
          (key, options) => translate(key, options),
          item.missingVariables,
        )
          .map(escapeHtml)
          .join(", ")}`
      : "";
    return ` <strong>[${translate(
      "oceanWeatherIntelligence.rowState.neverWarms",
    )}${missing}]</strong>`;
  }
  if (item?.rowState && item.rowState !== "ready") {
    return ` <strong>[${translate(
      PLAIN_ROW_STATE_LABELS[item.rowState],
    )}]</strong>`;
  }
  if (item?.error_message) {
    return ` <strong>[${translate("oceanWeatherIntelligence.error")}]</strong>`;
  }
  if (item?.isProcessing) {
    return ` <strong>[${translate(
      "oceanWeatherIntelligence.processing",
    )}]</strong>`;
  }
  return "";
};
